import {
	Cell,
	Column,
	Row,
	SearchField,
	TableBody,
	TableHeader,
	TableView,
	Text,
} from '@react-spectrum/s2';
import PeopleGroup from '@react-spectrum/s2/icons/PeopleGroup';
import {
	iconStyle,
	style,
} from '@react-spectrum/s2/style' with { type: 'macro' };
import { memo, useMemo, useState } from 'react';
import type { Key } from 'react';
import type { SortDescriptor } from 'react-aria-components';

import { AnimateIn } from '@/components/AnimateIn';
import { EmptyState } from '@/spaces/people/EmptyState';

import type { Audience } from './types';
import { getMemberCount } from './utils';

interface AudiencesListProps {
	audiences: Audience[];
	onAudienceClick: (audience: Audience) => void;
	onCreate?: () => void;
	onLoadMore?: () => void;
	isLoading?: boolean;
	isLoadingMore?: boolean;
	hasMore?: boolean;
	error?: string | null;
}

export const AudiencesList = memo(function AudiencesList({
	audiences,
	onAudienceClick,
	onCreate,
	onLoadMore,
	isLoading = false,
	isLoadingMore = false,
	hasMore = false,
	error = null,
}: AudiencesListProps) {
	const [searchQuery, setSearchQuery] = useState('');
	const [sortDescriptor, setSortDescriptor] = useState<SortDescriptor>({
		column: 'name',
		direction: 'ascending',
	});

	const filteredAudiences = useMemo(() => {
		if (!searchQuery.trim()) return audiences;
		const query = searchQuery.toLowerCase();
		return audiences.filter(
			(audience) =>
				audience.name.toLowerCase().includes(query) ||
				audience.description?.toLowerCase().includes(query),
		);
	}, [audiences, searchQuery]);

	const sortedAudiences = useMemo(() => {
		const list = [...filteredAudiences];
		const { column, direction } = sortDescriptor;
		list.sort((a, b) => {
			let cmp = 0;
			if (column === 'members') {
				cmp = getMemberCount(a) - getMemberCount(b);
			} else {
				cmp = (a.name || '').localeCompare(b.name || '', undefined, {
					sensitivity: 'base',
				});
			}
			return direction === 'descending' ? -cmp : cmp;
		});
		return list;
	}, [filteredAudiences, sortDescriptor]);

	if (isLoading) {
		return (
			<div
				className="flex h-full flex-col items-center justify-center gap-4 py-16"
			>
				<Text
					styles={style({
						font: 'body',
						fontSize: 'body-lg',
						color: 'body',
					})}
				>
					Loading audiences...
				</Text>
			</div>
		);
	}

	if (audiences.length === 0) {
		return (
			<div className="flex h-full flex-col">
				{error && (
					<p className="mb-2 text-center text-sm text-red-400">{error}</p>
				)}
				<EmptyState
					icon={<PeopleGroup styles={iconStyle({ size: 'XL' })} />}
					heading="No audiences yet"
					subtext="Group contacts into audiences to target campaigns."
					ctaLabel={onCreate ? 'New audience' : undefined}
					onAction={onCreate}
				/>
			</div>
		);
	}

	const handleAction = (key: Key) => {
		const audience = sortedAudiences.find((a) => a.id === key);
		if (audience) onAudienceClick(audience);
	};

	return (
		<div className="flex h-full min-h-0 flex-col gap-6">
			{error && <p className="text-center text-sm text-red-400">{error}</p>}

			<AnimateIn delay={0}>
				<div className="w-full">
					<SearchField
						aria-label="Search audiences"
						placeholder="Search by name or description..."
						value={searchQuery}
						onChange={setSearchQuery}
					/>
				</div>
			</AnimateIn>

			<AnimateIn delay={1} className="flex min-h-0 flex-1 flex-col">
				{sortedAudiences.length === 0 && searchQuery ? (
					<Text
						styles={style({
							font: 'body',
							fontSize: 'body',
							color: 'body',
						})}
						UNSAFE_className="block py-8 text-center"
					>
						No audiences match &quot;{searchQuery}&quot;
					</Text>
				) : (
					<div
						className="flex min-h-0 flex-1 flex-col
							[&_[role=progressbar]]:scale-50 [&_[role=row]]:cursor-pointer"
					>
						<TableView
							aria-label="Audiences"
							styles={style({ width: 'full', height: 'full' })}
							onAction={handleAction}
							overflowMode="wrap"
							isQuiet
							density="spacious"
							sortDescriptor={sortDescriptor}
							onSortChange={setSortDescriptor}
							loadingState={isLoadingMore ? 'loadingMore' : 'idle'}
							onLoadMore={
								hasMore && !searchQuery && !isLoadingMore
									? onLoadMore
									: undefined
							}
						>
							<TableHeader>
								<Column id="name" isRowHeader defaultWidth="2fr" allowsSorting>
									Name
								</Column>
								<Column id="members" allowsSorting>
									Members
								</Column>
							</TableHeader>
							<TableBody items={sortedAudiences}>
								{(audience) => {
									const name = audience.name || 'Untitled';
									const memberCount = getMemberCount(audience);
									return (
										<Row id={audience.id}>
											<Cell>{name}</Cell>
											<Cell>
												{memberCount} {memberCount === 1 ? 'member' : 'members'}
											</Cell>
										</Row>
									);
								}}
							</TableBody>
						</TableView>
					</div>
				)}
			</AnimateIn>
		</div>
	);
});
